import { createAsyncThunk, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { Draft } from 'immer';
import { entries, groupBy, now, sortBy } from 'lodash';
import { RootState } from './rootState';
import { closeTab, hasUnsavedChanges } from '../chrome/chromeActions';
import { fetchDBPages, PageSortOrder } from '../db/pages';
import { NormalizedUrl, normalizeUrl } from '../chrome/normalizeUrl';
import { shouldIgnore } from '../db/urlNormalizationPrefs';
import { isNotEmpty } from '../shared/utils';
import { setSimilarTags, setSimilarUrls, setTabIndex, selectSelectedUrls, clearSelectionAndSelect, constructPageRowId, setAllTabSortOrder } from './uiSlice';
import { logger } from '../utils/logger';

export interface Window {
  tabIds: Array<number>;
  activeTabId?: number;
  accessTime: number;
}

function newWindow() {
  return { tabIds: [], accessTime: Date.now() };
}

export interface Tab {
  id?: number;
  windowId?: number;
  title?: string;
  url?: NormalizedUrl;
  accessTime: number;
}

type TabsById = { [key: number]: Tab };

export interface WindowsState {
  tabs: TabsById; // tabId to Tab
  windows: { [key: number]: Window };
  focusedWindowId: number | undefined;
  desynced: boolean;
  recentTabs: { [key: string]: number }; // url to last access time
}

export const initialState: WindowsState = {
  windows: {},
  tabs: {},
  focusedWindowId: undefined,
  desynced: false,
  recentTabs: {},
};

function updateTab(state: Draft<WindowsState>, chromeTab: chrome.tabs.Tab, recordAccessTime = true) {
  const tabId = chromeTab.id;
  if (!tabId || !chromeTab.url) {
    return;
  }
  // removeTag([chromeTab.url], 'Stash');  // This causes problems-- some race condition?
  trimRecentTabs(state);
  const url = normalizeUrl(chromeTab.url);
  const accessTime = now();
  if (recordAccessTime && isNotEmpty(url.normalized) && !shouldIgnore(chromeTab.url)) {
    // console.log('state.recentTabs', state.recentTabs);
    state.recentTabs[url.normalized] = accessTime;
  }
  state.tabs[tabId] = {
    id: chromeTab.id,
    windowId: chromeTab.windowId,
    title: chromeTab.title,
    url: url,
    accessTime,
  };
}

function trimRecentTabs(state: Draft<WindowsState>) {
  const retainSeconds = 60;
  const cutoff = now() - retainSeconds * 1000;
  for (const url in state.recentTabs) {
    if (state.recentTabs[url] < cutoff) {
      delete state.recentTabs[url];
    }
  }
}

function overwriteActiveTabs(
  oldState: Draft<WindowsState>,
  windows: chrome.windows.Window[],
  storedAccessTimes: { [windowId: number]: number } = {}
): WindowsState {
  const overwriteStart = performance.now();
  // clear local state (but not 'pages' state in the db!)
  const state = { ...initialState };
  state.windows = {};
  state.tabs = {};
  state.recentTabs = {};

  windows.forEach((window) => {
    if (window.id == null || window.incognito) {
      return;
    }
    const windowId = window.id;
    state.windows[windowId] = newWindow();

    const best = Math.max(
      storedAccessTimes[windowId] ?? 0,
      oldState.windows[windowId]?.accessTime ?? 0
    );
    if (best > 0) {
      state.windows[windowId].accessTime = best;
    }

    window.tabs?.forEach((tab) => {
      if (!tab.id) {
        return;
      }
      if (state.windows[windowId].tabIds.includes(tab.id)) {
        // already there
      } else {
        state.windows[windowId].tabIds.push(tab.id);
      }
      if (tab.active) {
        state.windows[windowId].activeTabId = tab.id;
      }
      updateTab(state, tab, false);
    });
  });

  state.focusedWindowId = undefined;
  state.desynced = false;
  logger.debug(`overwriteActiveTabs: ${(performance.now() - overwriteStart).toFixed(0)} ms`);
  return state;
}

function ensureWindow(state: Draft<WindowsState>, windowId: number) {
  if (state.windows == null || state.windows[windowId] == null) {
    state.windows[windowId] = newWindow();
    state.desynced = true;
    logger.warn(`[WindowsSlice] Desynced: window ${windowId} not found`);
  }
}

export const updateWindowsAsync = createAsyncThunk('windows/update', async () => {
  logger.debug(`[WindowsSlice] updateWindowsAsync`);
  const [windows, stored] = await Promise.all([
    chrome.windows.getAll({ populate: true }),
    chrome.storage.session.get(null),
  ]);
  const storedAccessTimes: { [windowId: number]: number } = {};
  for (const [key, value] of Object.entries(stored)) {
    if (key.startsWith('wt_') && typeof value === 'number') {
      storedAccessTimes[Number(key.slice(3))] = value;
    }
  }

  // Derive window access times from DB page history as a fallback for when
  // chrome.storage.session is cleared on extension reload. For each window,
  // take the max last-activation timestamp across all its tabs.
  const urlToWindowId: { [url: string]: number } = {};
  for (const window of windows) {
    if (window.id == null || window.incognito) continue;
    for (const tab of window.tabs ?? []) {
      if (!tab.url || shouldIgnore(tab.url)) continue;
      const nurl = normalizeUrl(tab.url);
      if (nurl.normalized) {
        urlToWindowId[nurl.normalized] = window.id;
      }
    }
  }
  const allUrls = Object.keys(urlToWindowId);
  if (allUrls.length > 0) {
    const dbPages = await fetchDBPages(allUrls);
    for (const page of dbPages) {
      const windowId = urlToWindowId[page.url];
      if (windowId == null) continue;
      // lastActiveDesc is stored as -max(accessTimes); recover the actual timestamp
      const lastActive = -page.lastActiveDesc;
      if (lastActive > 0) {
        storedAccessTimes[windowId] = Math.max(storedAccessTimes[windowId] ?? 0, lastActive);
      }
    }
  }

  logger.debug(`[WindowsSlice] updateWindowsAsync done`);
  return { windows, storedAccessTimes };
});

function getOpenTabs(state: RootState, retainWindowId?: number) {
  const tabs: Tab[] = [];
  for (const windowId in state.windows.windows) {
    if (Number(windowId) == retainWindowId) {
      continue;
    }
    const window = state.windows.windows[windowId];
    window.tabIds.forEach((tabId) => tabs.push(state.windows.tabs[tabId]));
  }
  return tabs;
}

import { enqueueOperation } from '../db/queue';

export const stashWindowsAsync = createAsyncThunk(
  'windows/stashWindows',
  async (retainWindowId: number | undefined, thunkApi) => {
    // Offload to background queue
    await enqueueOperation('stash', { retainWindowId });
    // Note: We don't await completion here, so UI might not update immediately if it relied on local state changes.
    // However, since we are closing tabs, Chrome events will trigger 'removed' updates anyway.
  }
);

export const cleanupAsync = createAsyncThunk(
  'windows/cleanup',
  async (retainWindowId: number | undefined, thunkApi) => {
    // Offload to background queue
    await enqueueOperation('cleanup', { retainWindowId });
  }
);

// Worker singleton for similarity computation — created lazily, reused across calls.
// The worker reads IndexedDB directly and posts the result back when done.
let _similarityWorker: Worker | null = null;

// Monotonically-incrementing generation counter: each call to setSimilarUrlsAsync
// bumps this. After the startup delay, a call checks whether it has been superseded
// and exits early if so, preventing redundant worker dispatches.
let _computeGeneration = 0;

function getSimilarityWorker(dispatch: (action: ReturnType<typeof setSimilarUrls | typeof setSimilarTags>) => void): Worker {
  if (!_similarityWorker) {
    _similarityWorker = new Worker(
      new URL('../workers/similarityWorker.ts', import.meta.url),
      { type: 'module' }
    );
    _similarityWorker.addEventListener('message', (event: MessageEvent) => {
      if (event.data.type === 'result') {
        dispatch(setSimilarUrls(event.data.similarUrls));
        dispatch(setSimilarTags(event.data.similarTags));
      }
    });
    _similarityWorker.addEventListener('error', (e: ErrorEvent) => {
      logger.error('[SimilarityWorker] Uncaught worker error:', e.message);
      _similarityWorker?.terminate();
      _similarityWorker = null;
    });
  }
  return _similarityWorker;
}

export const setSimilarUrlsAsync = createAsyncThunk('windows/similarUrls', async (_, thunkAPI) => {
  const state = thunkAPI.getState() as RootState;
  if (!state.ui.autoComputeSimilarity) return;

  thunkAPI.dispatch(setSimilarUrls({}));

  const worker = getSimilarityWorker(thunkAPI.dispatch);

  // Invalidate any in-flight computation in the worker
  worker.postMessage({ type: 'abort' });

  if (state.ui.selectedUrls.length === 0) return;

  // Let other async tasks (db writes, liveQuery updates) settle before starting
  // the similarity computation. If a newer call arrives during this wait it will
  // increment _computeGeneration and we bail out, avoiding redundant work.
  const generation = ++_computeGeneration;
  await new Promise<void>((resolve) => setTimeout(resolve, 1000));
  if (_computeGeneration !== generation) return;

  // Re-read state after the delay so we use the freshest selection.
  const freshState = thunkAPI.getState() as RootState;
  const selectedUrls = freshState.ui.selectedUrls;
  if (selectedUrls.length === 0) return;

  const taskId = freshState.windows.focusedWindowId
    ? `window-${freshState.windows.focusedWindowId}-similarity`
    : `background-similarity-${Date.now()}`;
  const source = freshState.windows.focusedWindowId
    ? `window-${freshState.windows.focusedWindowId}`
    : 'background';

  worker.postMessage({ type: 'compute', selectedUrls, taskId, source });
  // Result dispatched asynchronously via the worker's 'message' listener above.
});

export const searchSimilarAsync = createAsyncThunk(
  'ui/searchSimilar',
  async (startUrl: NormalizedUrl | undefined, thunkAPI) => {
    if (!startUrl) return;

    const id = constructPageRowId(startUrl.normalized);
    thunkAPI.dispatch(clearSelectionAndSelect({ url: startUrl, id }));
    thunkAPI.dispatch(setAllTabSortOrder(PageSortOrder.similarity));
    thunkAPI.dispatch(setTabIndex(6));
    thunkAPI.dispatch(setSimilarUrlsAsync());
  }
);

export const windowsSlice = createSlice({
  name: 'windows',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    activated: (
      state,
      action: PayloadAction<{ windowId: number; tabId: number; chromeTab: chrome.tabs.Tab }>
    ) => {
      const windowId = action.payload.windowId;
      ensureWindow(state, windowId);
      const tabId = action.payload.tabId;
      state.focusedWindowId = windowId;
      state.windows[windowId].activeTabId = tabId;
      state.windows[windowId].accessTime = now();
      updateTab(state, action.payload.chromeTab);
      // TODO: sanity-check that the tab is already in the window
    },
    attached: (
      state,
      action: PayloadAction<{ tabId: number; newWindowId: number; newPosition: number }>
    ) => {
      const { tabId, newWindowId, newPosition } = action.payload;
      // Remove the tab from any window that currently holds it. This guards against the async
      // race where the 'attached' listener fires before the corresponding 'detached' listener
      // (both do an async checkIgnore call, so ordering is not guaranteed).
      for (const wId in state.windows) {
        if (Number(wId) !== newWindowId) {
          const updated = state.windows[wId].tabIds.filter(id => id !== tabId);
          if (updated.length !== state.windows[wId].tabIds.length) {
            if (updated.length === 0) {
              delete state.windows[wId];
            } else {
              state.windows[wId].tabIds = updated;
            }
          }
        }
      }
      ensureWindow(state, newWindowId);
      const existingIndex = state.windows[newWindowId].tabIds.indexOf(tabId);
      if (existingIndex !== -1) {
        state.windows[newWindowId].tabIds.splice(existingIndex, 1);
      }
      state.windows[newWindowId].tabIds.splice(newPosition, 0, tabId);
      state.tabs[tabId].windowId = newWindowId;

      // Also do "activated" stuff
      state.focusedWindowId = newWindowId;
      state.windows[newWindowId].activeTabId = tabId;
      state.windows[newWindowId].accessTime = now();
    },
    created: (state, action: PayloadAction<{ chromeTab: chrome.tabs.Tab }>) => {
      const chromeTab = action.payload.chromeTab;
      if (chromeTab.id) {
        ensureWindow(state, chromeTab.windowId);
        updateTab(state, chromeTab);
        const existingIndex = state.windows[chromeTab.windowId].tabIds.indexOf(chromeTab.id);
        if (existingIndex !== -1) {
          state.windows[chromeTab.windowId].tabIds.splice(existingIndex, 1);
        }
        state.windows[chromeTab.windowId].tabIds.splice(chromeTab.index, 0, chromeTab.id);
      }
    },
    detached: (
      state,
      action: PayloadAction<{ tabId: number; oldWindowId: number; oldPosition: number }>
    ) => {
      const { tabId, oldWindowId } = action.payload;
      ensureWindow(state, oldWindowId);
      // Filter by tabId (not splice by position) — position can be stale if tabs were reordered.
      // Matches the same pattern used by the `removed` reducer.
      state.windows[oldWindowId].tabIds = state.windows[oldWindowId].tabIds.filter(id => id !== tabId);
      if (state.windows[oldWindowId].tabIds.length == 0) {
        delete state.windows[oldWindowId];
      }
      if (state.tabs[tabId]) state.tabs[tabId].windowId = undefined;
    },
    moved: (
      state,
      action: PayloadAction<{
        tabId: number;
        windowId: number;
        fromIndex: number;
        toIndex: number;
      }>
    ) => {
      const { tabId, windowId, fromIndex, toIndex } = action.payload;
      ensureWindow(state, windowId);
      const existingIndex = state.windows[windowId].tabIds.indexOf(tabId);
      if (existingIndex !== -1) {
        state.windows[windowId].tabIds.splice(existingIndex, 1);
      }
      state.windows[windowId].tabIds.splice(toIndex, 0, tabId);

      // Also do "activated" stuff
      state.focusedWindowId = windowId;
      state.windows[windowId].activeTabId = tabId;
      state.windows[windowId].accessTime = now();
      // updateTab(state, action.payload.chromeTab);
    },
    removed: (
      state,
      action: PayloadAction<{ tabId: number; windowId: number; isWindowClosing: boolean }>
    ) => {
      const { tabId, windowId, isWindowClosing } = action.payload;
      ensureWindow(state, windowId);
      state.windows[windowId].tabIds = state.windows[windowId].tabIds.filter((x) => x != tabId);

      delete state.tabs[tabId];
    },
    replaced: (state, action: PayloadAction<{ addedTabId: number; removedTabId: number }>) => {
      const { addedTabId, removedTabId } = action.payload;
      for (const windowId in state.windows) {
        state.windows[windowId].tabIds = state.windows[windowId].tabIds.map((tabId) =>
          tabId == removedTabId ? addedTabId : tabId
        );
      }
      state.tabs[addedTabId] = state.tabs[removedTabId];
      delete state.tabs[removedTabId];
    },
    updated: (
      state,
      action: PayloadAction<{
        tabId: number;
        changeInfo: chrome.tabs.TabChangeInfo;
        chromeTab: chrome.tabs.Tab;
      }>
    ) => {
      // Chrome may report a transient URL (e.g. about:blank) when discarding a tab.
      // Preserving the existing Redux state prevents a blank row in the Open view.
      if (action.payload.changeInfo.discarded) return;
      updateTab(state, action.payload.chromeTab);
    },
    zoomChanged: (
      state,
      action: PayloadAction<{
        tabId: number;
        oldZoomFactor: number;
        newZoomFactor: number;
        zoomSettings: chrome.tabs.ZoomSettings;
      }>
    ) => {},
    windowBoundsChanged: (state, action: PayloadAction<{ window: chrome.windows.Window }>) => {},
    windowCreated: (state, action: PayloadAction<{ window: chrome.windows.Window }>) => {
      const window = action.payload.window;
      if (window.id && !state.windows[window.id]) {
        state.windows[window.id] = newWindow();
      }
    },
    windowRemoved: (state, action: PayloadAction<{ windowId: number }>) => {
      const windowId = action.payload.windowId;
      if (windowId && state.windows[windowId]) {
        delete state.windows[windowId];
      }
    },
    windowFocused: (state, action: PayloadAction<{ windowId: number | undefined }>) => {
      const windowId = action.payload.windowId;
      state.focusedWindowId = windowId;
      if (windowId) {
        ensureWindow(state, windowId);
        state.windows[windowId].accessTime = now();
      }
    },
    dedupAll: (state) => {
      const dgs = duplicateGroups(state.tabs);
      for (const [url, tabs] of dgs) {
        const tt = [...tabs];
        const ss = sortBy(tt, ([tabId, tab]) => tab.accessTime);
        const oldest = ss.shift();
        for (const [tabId, tab] of ss) {
          closeTab(tabId);
        }
      }
    },
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder.addCase(updateWindowsAsync.fulfilled, (state, action) =>
      overwriteActiveTabs(state, action.payload.windows, action.payload.storedAccessTimes)
    );
  },
});

export const selectWindows = (state: RootState) => state.windows.windows;

export const selectWindow = (state: RootState, windowId: number) => state.windows.windows[windowId];

// export const selectWindow = createSelector(
//   [selectWindows, (state, windowId: number) => windowId],
//   (windows, windowId: number) => windows[windowId]
// );

// Excludes the Tabvana window itself from the count.
export const selectWindowsCount = createSelector(
  [selectWindows, (_state: RootState, tabvanaWindowId: number | undefined) => tabvanaWindowId],
  (windows, tabvanaWindowId) =>
    Object.keys(windows).filter((id) => Number(id) !== tabvanaWindowId).length
);

export const selectTabs = (state: RootState) => state.windows.tabs;

// Counts tabs across all windows except the Tabvana window.
// Uses window.tabIds rather than the tabs map directly to avoid a timing issue: the Tabvana
// tab (chrome-extension:// URL) is filtered by shouldIgnore in overwriteActiveTabs but can
// be transiently present in the tabs map when added by event listeners (onCreated, onActivated)
// before the next sync. Counting from the windows map avoids that race.
export const selectTabsCount = createSelector(
  [selectWindows, (_state: RootState, tabvanaWindowId: number | undefined) => tabvanaWindowId],
  (windows, tabvanaWindowId) =>
    Object.entries(windows)
      .filter(([id]) => Number(id) !== tabvanaWindowId)
      .reduce((sum, [, w]) => sum + w.tabIds.length, 0)
);

// export const selectTabsForWindow = createSelector(
//   [selectWindows, selectTabs, (state, windowId: number) => windowId],
//   (windows, tabs, windowId) => {
//     return windows[windowId].tabIds
//       .map((tabId) => (tabId ? tabs[tabId] : null))
//       .filter(Boolean) as Tab[];
//   }
// );

export const makeSelectTabsForWindow = () =>
  createSelector(
    [selectWindows, selectTabs, (state, windowId: number) => windowId],
    (windows, tabs, windowId) => {
      // console.log(`makeSelectTabsForWindow(${windowId})`);
      const uniqueTabIds = [...new Set(windows[windowId].tabIds)];
      return uniqueTabIds
        .map((tabId) => (tabId ? tabs[tabId] : null))
        .filter(Boolean) as Tab[];
    }
  );

// export const selectNormalizedUrlsForWindow = createSelector(
//   [selectTabsForWindow],
//   (tabs) => tabs.map((tab) => tab.url).filter(Boolean) as NormalizedUrl[]
// );

// export const makeSelectNormalizedUrlsForWindow = (selectTabsForWindow) => {
//   // const selectTabsForWindow = makeSelectTabsForWindow();
//   const selectNormalizedUrlsForWindow = createSelector(
//     [selectTabsForWindow],
//     (tabs) => tabs.map((tab) => tab.url).filter(Boolean) as NormalizedUrl[]
//   );
//   return selectNormalizedUrlsForWindow;
// }

export const selectFocusedWindowId = (state: RootState) => state.windows.focusedWindowId;

export const selectIsActiveWindow = (state: RootState) => (windowId: number) =>
  state.windows.focusedWindowId == windowId;

export const selectIsActiveTab = createSelector(
  [
    selectWindows,
    (state, windowId: number | undefined, tabId: number | undefined) => windowId,
    (state, windowId: number | undefined, tabId: number | undefined) => tabId,
  ],
  (windows, windowId, tabId) => {
    if (!tabId || !windowId) {
      return false;
    }
    const activeWindow = windows[windowId];
    if (activeWindow == null) {
      return false;
    }

    return activeWindow.activeTabId == tabId;
  }
);

export const selectIsActivePage = createSelector(
  [selectWindows, selectTabs, (state, url: NormalizedUrl | undefined) => url],
  (windows, tabs, url) => {
    if (!url) {
      return false;
    }
    for (const windowId in windows) {
      const activeTabId = windows[windowId].activeTabId;
      if (activeTabId && tabs[activeTabId].url == url) {
        return true;
      }
    }
    return false;
  }
);

export const selectIsFocusedPage = createSelector(
  [selectWindows, selectFocusedWindowId, selectTabs, (state, url: string | undefined) => url],
  (windows, focusedWindowId, tabs, url) => {
    if (!url) {
      return false;
    }
    if (!focusedWindowId) {
      return false;
    }
    const activeTabId = windows[focusedWindowId]?.activeTabId;
    return activeTabId && tabs[activeTabId]?.url?.normalized == url;
  }
);

export const selectTabsForPage = createSelector(
  selectTabs,
  (state: RootState, url: NormalizedUrl) => url,
  (tabs, url) => {
    const filteredTabs = entries(tabs)
      .filter(([tabId, tab]) => tab != null && tab.url?.normalized == url.normalized)
      .map(([tabId, tab]) => tab);
    return filteredTabs;
  }
);

function duplicateGroups(tabs: TabsById) {
  const nonNullTabs = entries(tabs)
    .filter(([tabId, tab]) => tab != null && tab.id != null && tab.url != null)
    .map(([tabId, tab]) => [Number(tabId), tab] as [number, Tab]);
  const groups = groupBy(nonNullTabs, ([tabId, tab]) => tab.url?.normalized);
  return Object.entries(groups).filter(([url, tabs]) => tabs.length > 1);
}

export const selectDuplicateUrls = createSelector(
  (state: RootState) => state.windows.tabs,
  (tabs) => duplicateGroups(tabs).map(([url, tabs]) => url)
);

export const selectIsDuplicate = createSelector(
  (state: RootState) => selectDuplicateUrls(state),
  (state: RootState, url: NormalizedUrl) => url,
  (allDuplicates, url?: NormalizedUrl): boolean => {
    return isNotEmpty(url?.normalized) && allDuplicates.includes(url.normalized);
  }
);

export const isDesynced = (state: RootState) => state.windows.desynced;

export const {
  activated,
  attached,
  created,
  detached,
  moved,
  removed,
  replaced,
  updated,
  zoomChanged,
  windowBoundsChanged,
  windowCreated,
  windowFocused,
  windowRemoved,
  dedupAll,
} = windowsSlice.actions;

export const dedupTabsAsync = createAsyncThunk(
  'windows/dedupTabsAsync',
  async (_arg: void, { getState }) => {
    const state = getState() as RootState;
    const dgs = duplicateGroups(state.windows.tabs);
    let skipped = 0;
    for (const [, tabs] of dgs) {
      const tt = [...tabs];
      const ss = sortBy(tt, ([, tab]) => tab.accessTime);
      ss.shift(); // keep the oldest
      for (const [tabId] of ss) {
        if (await hasUnsavedChanges(tabId)) {
          skipped++;
        } else {
          closeTab(tabId);
        }
      }
    }
    return { skipped };
  }
);

export default windowsSlice;
